import { defineStore } from 'pinia'
// @ts-expect-error: vuex - temporary for child edition operations
import { useStore } from 'vuex'
import { ref, computed } from 'vue'
import isNil from 'lodash.isnil'
import isString from 'lodash.isstring'
import merge from 'lodash.merge'
import omitBy from 'lodash.omitby'

import { useChildEditionStore } from '@/stores/childEdition'
import { AuditCommentSchema } from '@/schemas/comment'
import resourceService from '@/services/http/api/resource'
import successCardService from '@/services/http/api/successCard'
import { useErrorModal } from '@/composables/useErrorModal'
import { sleep } from '@/helpers/sleep'

import {
  shouldRetryGetActionForAssignedEndpoint,
  shouldRetryPostActionForAssignedEndpoint
} from '@/helpers/retryActionForOtherEndpoint'

import type { TODO, Maybe } from '@/types'
import formRadioValue from '@/constants/formRadioValue'

const booleanRadios = ['meetingTookPlace'] as const

const prepareData = (payload: TODO) => {
  if (payload) {
    const data = { ...payload }
    data.data = omitBy(payload.data, isNil)
    data.data = omitBy(data.data, (value: TODO) => value?.length === 0)
    booleanRadios.forEach((item) => {
      if (data.data[item]) {
        data.data[item] = data.data[item] === formRadioValue.yes ? 1 : 0
      }
    })
    return data
  }
  return {}
}

const modifyPayloadForSuccessCard = (payload: TODO): TODO => {
  const successCardPayload = { ...payload }
  booleanRadios.forEach((item) => {
    if (successCardPayload?.content?.[item] || successCardPayload?.content?.[item] === 0) {
      successCardPayload.content[item] =
        successCardPayload?.content?.[item] === 1 ? formRadioValue.yes : formRadioValue.no
    }
  })
  return successCardPayload
}

const emptySuccessCardForm = (): TODO => ({
  meetingTookPlace: null,
  meetingDate: null,
  meetingType: null,
  lessonType: null,
  catchUpMeetingDate: null,
  successDescription: null,
  meetingPurpose: [],
  meetingDescription: null,
  meetingConclusions: null,
  plannedMeetingDate: null,
  cancellationReason: null
})

export const useSuccessCardStore = defineStore('successCard', () => {
  const { setErrorModalOnError } = useErrorModal()
  const loader = ref<boolean>(false)
  const sendLoader = ref<boolean>(false)
  const auditLoader = ref<boolean>(false)
  const sendToGiftGiverLoader = ref<boolean>(false)
  const commentLoader = ref<boolean>(false)
  const commentSuccess = ref<boolean>(false)
  const successCard = ref<Maybe<TODO>>(null)
  const resource = ref<Maybe<TODO>>(null)
  const successCardForm = ref<TODO>(emptySuccessCardForm())
  const editMode = ref<boolean>(false)

  const childEditionStore = useChildEditionStore()
  // TODO: Remove this when child edition store is fully migrated to Pinia
  const vuexStore = useStore()
  const getComments = computed(() => {
    return (
      successCard.value?.audit_comments?.filter((comment: TODO) => {
        const parsedComment = AuditCommentSchema.safeParse(comment)
        return parsedComment.success
      }) ?? []
    )
  })

  const setResource = (payload: TODO) => {
    resource.value = merge(resource.value, payload)
  }
  const setSuccessCard = (payload: TODO) => {
    successCard.value = { ...successCard.value, ...payload }
  }
  const setSuccessCardForm = (payload: TODO) => {
    if (!payload) return
    Object.entries(successCardForm.value).forEach(([key]) => {
      successCardForm.value[key] = payload[key]
    })
  }
  const setSuccessCardAndSuccessCardForm = (payload: TODO) => {
    setSuccessCard({ ...payload })
    setSuccessCardForm({ ...payload?.content })
  }
  const modifyPayloadAndSetSuccessCard = (payload: TODO) => {
    const modifiedSuccessCardPayload = modifyPayloadForSuccessCard(payload)
    setSuccessCardAndSuccessCardForm(modifiedSuccessCardPayload)
  }
  const setAuditComments = (payload: TODO) => {
    if (!successCard.value) {
      throw new Error('No success card to set audit comments')
    }
    successCard.value.audit_comments = payload
  }
  const clear = () => {
    successCard.value = null
    loader.value = false
    editMode.value = false
    resource.value = null
  }
  const clearSuccessCardForm = () => {
    successCardForm.value = emptySuccessCardForm()
  }

  const initEditionName = async (payload: TODO) => {
    try {
      return await childEditionStore.getChildsEditionName(payload.childEditionId)
    } catch (error) {
      return Promise.reject(error)
    }
  }
  const fetchSuccessCard = async (payload: TODO) => {
    try {
      const { data } = await successCardService.get(payload, payload.getAssignedFirst)
      const responseData = data?.data
      modifyPayloadAndSetSuccessCard(responseData)
      loader.value = false
      return responseData
    } catch (error) {
      if (shouldRetryGetActionForAssignedEndpoint(error, payload)) {
        return fetchSuccessCard({ ...payload, getAssignedFirst: true })
      }
      return Promise.reject(error)
    }
  }
  const initDataForSuccessCardPage = async (payload: TODO) => {
    try {
      const getSuccessCardResponse = fetchSuccessCard(payload)

      return [await getSuccessCardResponse]
    } catch (error) {
      return Promise.reject(error)
    }
  }
  const fetchResource = async (payload: string) => {
    try {
      const { data } = await resourceService.successCard(payload)
      setResource(data.data)
      return data.data
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const init = async (payload: TODO) => {
    clear()
    clearSuccessCardForm()

    loader.value = true

    const editionName = await initEditionName(payload)
    // editionName should be a non empty string to get resources
    if (!(isString(editionName) && editionName?.length > 0)) return null
    await fetchResource(editionName)

    if (payload.routeName === 'SuccessCard') {
      await initDataForSuccessCardPage(payload)
    }

    if (payload.routeName === 'SuccessCardAdd') {
      editMode.value = true
    }

    // finall cleanup
    loader.value = false
    return null
  }

  const responseFunction = (data: TODO) => {
    const { school_hydra_users: schoolHydraUsers } =
      vuexStore.getters['childEdition/getChild'] ?? {}

    const childEdition = data?.data?.child_edition

    if (childEdition && typeof childEdition === 'object') {
      vuexStore.commit(
        'childEdition/setChild',
        {
          child_edition: childEdition,
          school_hydra_users: schoolHydraUsers
        },
        { root: true }
      )
    }

    vuexStore.commit('childEdition/setChildEditionRequestSkipped', true, { root: true })
    modifyPayloadAndSetSuccessCard(data?.data?.success_card ?? data?.data?.successCard)
    return data
  }

  const add = async (payload: TODO) => {
    sendLoader.value = true
    const payloadData = prepareData(payload)
    try {
      const { data } = await successCardService.add(payloadData, payloadData.postAssignedFirst)
      return responseFunction(data)
    } catch (error) {
      if (shouldRetryPostActionForAssignedEndpoint(error, payload)) {
        return await add({ ...payload, postAssignedFirst: true })
      } else setErrorModalOnError(error)
    } finally {
      sendLoader.value = false
    }
  }

  const editAddedSuccessCard = async (payload: TODO) => {
    sendLoader.value = true
    const payloadData = prepareData(payload)
    try {
      const { data } = await successCardService.editAddedSuccessCard(payloadData)
      return responseFunction(data)
    } catch (error) {
      setErrorModalOnError(error)
    } finally {
      sendLoader.value = false
    }
  }
  const privilegedUpdate = async (payload: TODO) => {
    sendLoader.value = true
    const payloadData = prepareData(payload)
    try {
      const { data } = await successCardService.privilegedUpdate(payloadData)
      return responseFunction(data)
    } catch (error) {
      setErrorModalOnError(error)
    } finally {
      sendLoader.value = false
    }
  }
  const sendToGiftGiver = async (payload: TODO) => {
    sendToGiftGiverLoader.value = true
    try {
      const { data } = await successCardService.sendToGiftGiver(payload)
      return responseFunction(data)
    } catch (error) {
      setErrorModalOnError(error)
    } finally {
      sendToGiftGiverLoader.value = false
    }
  }
  const sendBack = async (payload: TODO) => {
    auditLoader.value = true
    try {
      const { data } = await successCardService.sendBack(payload)
      return responseFunction(data)
    } catch (error) {
      setErrorModalOnError(error)
    } finally {
      auditLoader.value = false
    }
  }
  const correctionSendBack = async (payload: TODO) => {
    auditLoader.value = true
    try {
      const { data } = await successCardService.correctionSendBack(
        payload,
        payload.postAssignedFirst
      )
      return responseFunction(data)
    } catch (error) {
      if (shouldRetryPostActionForAssignedEndpoint(error, payload)) {
        await correctionSendBack({ ...payload, postAssignedFirst: true })
      } else setErrorModalOnError(error)
    } finally {
      auditLoader.value = false
    }
  }

  const editSentBack = async (payload: TODO) => {
    sendLoader.value = true
    const payloadData = prepareData(payload)
    try {
      const { data } = await successCardService.editOnSentBack(
        payloadData,
        payloadData.postAssignedFirst
      )
      return responseFunction(data)
    } catch (error) {
      if (shouldRetryPostActionForAssignedEndpoint(error, payload)) {
        await editSentBack({ ...payload, postAssignedFirst: true })
      } else setErrorModalOnError(error)
    } finally {
      sendLoader.value = false
    }
  }
  const addComment = async (payload: TODO, successTimeout = 3500) => {
    commentLoader.value = true
    try {
      const { data: responseData } = await successCardService.addComment(
        payload,
        payload?.postAssignedFirst
      )

      setAuditComments(responseData?.data?.successCard?.audit_comments)
      commentSuccess.value = true
    } catch (error) {
      commentSuccess.value = false
      if (shouldRetryPostActionForAssignedEndpoint(error, payload)) {
        await addComment({ ...payload, postAssignedFirst: true })
      } else setErrorModalOnError(error)
    } finally {
      commentLoader.value = false
    }
    // Show the success (we use success icon in comment section)
    // and reset the flag after a timeout
    await sleep(successTimeout)
    commentSuccess.value = false
  }

  return {
    // state
    loader,
    sendLoader,
    auditLoader,
    sendToGiftGiverLoader,
    commentLoader,
    commentSuccess,
    successCard,
    resource,
    successCardForm,
    editMode,

    // getters
    getComments,

    // actions
    setResource,
    setSuccessCard,
    setAuditComments,
    clear,
    setSuccessCardForm,
    setSuccessCardAndSuccessCardForm,
    modifyPayloadAndSetSuccessCard,
    clearSuccessCardForm,
    initEditionName,
    fetchResource,
    fetchSuccessCard,
    initDataForSuccessCardPage,
    init,
    add,
    editAddedSuccessCard,
    privilegedUpdate,
    sendToGiftGiver,
    sendBack,
    correctionSendBack,
    editSentBack,
    addComment
  }
})
